import type { ReactNode } from 'react'
import React, { useEffect, useRef, useState } from 'react'
import {
  Drawer,
  Button,
  DateTimePicker,
  Modal,
  ToggleControl,
  toastManager,
} from '@systemeio/components'
import { PlusCircleIcon, XCloseIcon } from '@systemeio/icons'
import type { Emoji } from 'emoji-mart'
import { useTranslation } from 'next-i18next/pages'
import type { SubmitHandler, UseFormSetError } from 'react-hook-form'
import { Controller, FormProvider, useForm } from 'react-hook-form'
import { COMMUNITY_TITLE_MAX_LENGTH, EMBEDDED_VIDEO_URL_MAX_LENGTH } from 'shared/constants'
import { LocaleEnum, getLanguageCodeByLocale } from 'shared/enums/locale-enum'
import { BadRequest } from 'shared/errors/bad-request'
import { InternalError } from 'shared/errors/internal-error'
import { useFormatDateTime } from 'shared/hooks/use-format-date-time'
import { useIsMobileWhileOpen } from 'shared/hooks/use-is-mobile-while-open'
import useUser from 'shared/hooks/use-user'
import { determineTimezone } from 'shared/utils/date-time/determine-timezone'
import { usePostAttachmentsContext } from 'modules/attachments/components/context'
import { hasEmbeddedVideoAttachmentUrlTooLong } from 'modules/attachments/utils/attachment-utils'
import { useCreatePost } from 'modules/community/api/use-create-post'
import { isPublishedPostStatus } from 'modules/community/enums/post-status-enum'
import { generatePathFromName } from 'modules/community/hooks/path-utils'
import { useCommunityWithTopics } from 'modules/community/hooks/use-community-with-topics'
import type { PostInterface } from 'modules/community/types/post-interface'
import { hasPermission } from 'modules/community/utils/abac-roles-permissions'
import { escapeAttachmentLocalFields } from 'modules/community/utils/attachments'
import PostAttachments from './components/attachments'
import type { EditorRef } from './components/content'
import AddPostContentEditor from './components/content'
import AddPostInputField from './components/input-field'
import AddPostTopicSelect from './components/topic-select'
import type { CreatePostFormValues } from './types'
import { validateNotPastDate } from './validations'

const defaultValues: Omit<CreatePostFormValues, 'scheduledTo'> = {
  title: '',
  path: '',
  content: '',
}

type ErrorFields = Parameters<UseFormSetError<CreatePostFormValues>>[0]

const errorFields: ErrorFields[] = ['title', 'path', 'topic', 'content', 'root', 'scheduledTo']

interface AddPostProps {
  onAdd?: (post: PostInterface) => void
}

function AddPost({ onAdd }: AddPostProps): ReactNode {
  const { t } = useTranslation()
  const { user } = useUser()

  const [opened, setOpened] = useState(false)
  const [displaySchedulingOptions, setDisplaySchedulingOptions] = useState(false)
  const { data: community, isValidating } = useCommunityWithTopics()
  const { isUploading } = usePostAttachmentsContext()

  const createPostMutation = useCreatePost({
    communityPath: community?.path ?? '',
  })

  const formMethods = useForm<CreatePostFormValues>({ defaultValues })
  const canSchedulePosts = hasPermission(user, 'scheduledPosts', 'schedule', undefined)
  const timezone = determineTimezone(user)
  const scheduledTo = formMethods.watch('scheduledTo')
  const scheduledToFormatted = useFormatDateTime(scheduledTo)
  const editorRef = useRef<EditorRef>(null)
  const isMobile = useIsMobileWhileOpen(opened)

  function convertNameToPath(e: React.FocusEvent<HTMLInputElement>): void {
    if (!e.target.value) {
      formMethods.setValue('path', generatePathFromName(formMethods.getValues('title')))
    }
  }

  const handleAddEmoji = (emoji: typeof Emoji.Props): void =>
    editorRef.current?.insertEmoji(emoji.native)

  const onSubmit: SubmitHandler<CreatePostFormValues> = formData => {
    if (!community) return

    formMethods.clearErrors()
    const { title, path, content, topic, attachments, scheduledTo } = formData

    if (hasEmbeddedVideoAttachmentUrlTooLong(attachments)) {
      formMethods.setError('root', {
        message: t('post.attachments.embedded_video_link.error_too_long', {
          max: EMBEDDED_VIDEO_URL_MAX_LENGTH,
        }),
      })
      return
    }

    createPostMutation.mutate(
      {
        title,
        path,
        content,
        topic,
        scheduledTo,
        attachments: escapeAttachmentLocalFields(attachments),
      },
      {
        onSuccess: data => {
          if (scheduledTo) {
            toastManager.add({ type: 'success', title: t('community.add_post.scheduled_message') })
          } else if (!isPublishedPostStatus(data.status)) {
            toastManager.add({
              type: 'success',
              title: t('community.add_post.sent_to_approval_message'),
            })
          }

          onAdd?.(data)
          setOpened(false)
          formMethods.reset(defaultValues)
          editorRef.current?.clear()
          setDisplaySchedulingOptions(false)
        },
        onError: error => {
          if (error instanceof BadRequest) {
            errorFields.forEach(name => {
              const fieldError = error.errors?.fields?.[name]
              if (fieldError) {
                formMethods.setError(name, { message: fieldError.join('\n') })
              }

              if (name === 'root' && error.errors?.common) {
                formMethods.setError(name, { message: error.errors.common.join('\n') })
              }
            })
          } else if (error instanceof InternalError) {
            formMethods.setError('root', { message: t('core.error.internal_error_message') })
          }
        },
      },
    )
  }

  useEffect(() => {
    if (!opened) {
      formMethods.reset(defaultValues)
      editorRef.current?.clear()
      setDisplaySchedulingOptions(false)
      formMethods.clearErrors()
    }
  }, [opened])

  if (!community || isValidating) {
    return <div className="mb-10 h-28 animate-pulse rounded-lg bg-bg-tertiary" />
  }

  const title = t('settings.create_post.modal.title')

  const fields = (
    <>
      <AddPostInputField
        name="title"
        maxLength={COMMUNITY_TITLE_MAX_LENGTH}
        label={t('home.add_post.title')}
      />
      <AddPostInputField
        name="path"
        maxLength={32}
        onFocus={convertNameToPath}
        label={t('home.add_post.path')}
      />
      <AddPostTopicSelect />
      <AddPostContentEditor ref={editorRef} />
      <PostAttachments onAddEmoji={handleAddEmoji} />

      {canSchedulePosts && (
        <div data-testid="create-post-scheduling">
          <div className="mb-6 h-0 w-full border-t border-text-secondary/30" />

          <div className="mb-6">
            <div className="flex items-center gap-x-3">
              <ToggleControl
                onCheckedChange={setDisplaySchedulingOptions}
                checked={displaySchedulingOptions}
              />
              <p className="text-base font-medium text-text-primary">
                {t('home.add_post.scheduling_post.label')}
              </p>
            </div>
            {displaySchedulingOptions && (
              <div className="flex flex-col gap-1 pt-2">
                <p className="text-base text-text-primary/50">
                  {t('settings.schedule_post.modal.description')}
                </p>
                <Controller
                  name="scheduledTo"
                  control={formMethods.control}
                  shouldUnregister
                  defaultValue={null}
                  rules={{
                    required: {
                      value: true,
                      message: t('validation.date_time_required_error'),
                    },
                    validate: value => validateNotPastDate(t, value),
                  }}
                  render={({ field }) => (
                    <DateTimePicker
                      label={t('home.add_post.date_time_picker.label')}
                      value={field.value}
                      onChange={date => field.onChange(date ? new Date(date).toISOString() : null)}
                      timezone={timezone}
                      intlLocale={getLanguageCodeByLocale(
                        user?.dashboardLocale || LocaleEnum.ENGLISH,
                      )}
                      minDate={new Date()}
                    />
                  )}
                />
                {scheduledToFormatted && (
                  <p className="text-sm text-text-secondary">
                    {t('schedule_post.modal.scheduling_time_zone', {
                      label: scheduledToFormatted.title,
                    })}
                  </p>
                )}
                {formMethods.formState.errors.scheduledTo?.message && (
                  <p className="text-sm text-text-error">
                    {formMethods.formState.errors.scheduledTo.message}
                  </p>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </>
  )

  const action = (
    <Button
      variant="primary"
      type="submit"
      isLoading={createPostMutation.isPending}
      disabled={isUploading}
    >
      <PlusCircleIcon className="size-5" />
      {t('home.add_post.button.title')}
    </Button>
  )

  const rootError = (
    <>
      {formMethods.formState.errors.root?.message && (
        <p className="mt-2 text-sm text-text-error">{formMethods.formState.errors.root.message}</p>
      )}
    </>
  )

  return (
    <>
      <div className="mb-8 flex flex-col items-end bg-bg-primary p-5 lg:rounded-lg">
        <button
          type="button"
          data-testid="add-post-trigger"
          className="h-12 w-full cursor-pointer resize-none overflow-hidden rounded-md border border-text-secondary/30 px-5 text-start text-text-secondary focus:outline-hidden"
          onClick={() => setOpened(true)}
        >
          {t('home.add_post.text_area.placeholder')}
        </button>
      </div>
      {isMobile ? (
        <FormProvider {...formMethods}>
          <Drawer.Root open={opened} onOpenChange={value => setOpened(value)}>
            <Drawer.Popup render={<form onSubmit={formMethods.handleSubmit(onSubmit)} />}>
              <Drawer.Header className="flex-row items-start gap-2.5">
                <Drawer.Title className="min-w-0 flex-1">{title}</Drawer.Title>
                <Drawer.Close
                  aria-label="Close"
                  className="shrink-0"
                  render={<Button variant="ghost-gray" size="icon-sm" />}
                >
                  <XCloseIcon />
                </Drawer.Close>
              </Drawer.Header>
              <Drawer.Content className="flex flex-col gap-6">
                {fields}
                {rootError}
              </Drawer.Content>
              <Drawer.Footer>{action}</Drawer.Footer>
            </Drawer.Popup>
          </Drawer.Root>
        </FormProvider>
      ) : (
        <Modal.Root open={opened} onOpenChange={value => setOpened(value)}>
          <Modal.Popup
            className="m-auto max-h-none w-full max-w-4xl! shrink-0 overflow-visible"
            viewportProps={{ className: 'flex flex-col overflow-y-auto p-1' }}
            data-testid="create-post-modal"
          >
            <Modal.Header>
              <Modal.Title>{title}</Modal.Title>
            </Modal.Header>
            <FormProvider {...formMethods}>
              <form
                onSubmit={formMethods.handleSubmit(onSubmit)}
                className="flex flex-col px-6 pb-6 lg:px-10 lg:pb-10"
              >
                {fields}

                <div className="flex justify-end gap-x-2">{action}</div>
                {rootError}
              </form>
            </FormProvider>
          </Modal.Popup>
        </Modal.Root>
      )}
    </>
  )
}

export default AddPost
